import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getExportFileErrorMessage, useExportContactTracingFile } from '../../../api/created/exportFile.ts';
import { GetPatientsBasicsType, PatientBasicsDto } from '../../../api/generated/model';
import { getContactTracingExportMessage } from '../../../utils/contactTracingExportCode.ts';
import { SelectPatient } from '../../components/SelectPatient.tsx';

interface FormValues {
  patient: PatientBasicsDto | null;
}

export function ExportTracingContactsDialog({
  preselectedPatient,
  open,
  onClose,
}: {
  preselectedPatient: PatientBasicsDto | null;
  open: boolean;
  onClose: () => void;
}) {
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      patient: preselectedPatient,
    },
  });

  const patientValue = watch('patient');
  const [exportErrorMessage, setExportErrorMessage] = useState<string | null>(null);

  const exportContactTracingFileMutation = useExportContactTracingFile();

  useEffect(() => {
    if (open) {
      reset({ patient: preselectedPatient });
      setExportErrorMessage(null);
    }
  }, [open, preselectedPatient]);

  useEffect(() => {
    setExportErrorMessage(null);
  }, [patientValue]);

  const handleOnClose = () => {
    reset();
    setExportErrorMessage(null);
    onClose();
  };

  const onSubmit = async ({ patient }: FormValues) => {
    setExportErrorMessage(null);
    try {
      await exportContactTracingFileMutation.mutateAsync(patient!.id);
      handleOnClose();
    } catch (error) {
      setExportErrorMessage(await getExportFileErrorMessage(error, getContactTracingExportMessage));
    }
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Kontaktliste exportieren</Typography>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <FormControl error={!!errors.patient}>
              <FormLabel>
                <Typography>Kontakte von *</Typography>
              </FormLabel>
              {preselectedPatient ? (
                <Typography>
                  {preselectedPatient.caseNumber ?? `${preselectedPatient.lastName} ${preselectedPatient.firstName}`}
                </Typography>
              ) : (
                <Controller
                  name={'patient'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectPatient
                      onChange={onChange}
                      value={value}
                      patientsType={GetPatientsBasicsType.CONTACT_TRACING}
                      activeOnly
                      showCaseNumber
                    />
                  )}
                />
              )}
              {errors.patient && <FormHelperText>Indexpatient muss angegeben werden</FormHelperText>}
            </FormControl>

            {exportErrorMessage && (
              <Alert color={'danger'} startDecorator={<Icons.ErrorOutline />}>
                {exportErrorMessage}
              </Alert>
            )}

            <Divider inset="context" />
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button endDecorator={<Icons.Close />} color="neutral" onClick={handleOnClose}>
                Abbrechen
              </Button>
              <Button
                endDecorator={<Icons.Download />}
                type="submit"
                disabled={!patientValue}
                loading={exportContactTracingFileMutation.isPending}>
                Exportieren
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
